import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { toast } from "react-toastify";

import { ApiURLs } from "../../constant/ApiUrls";
import { fetchData, updateData, postData } from "../../services/api";

/**
 * The wording and the numbers behind the automatic notices.
 *
 * Everything on this screen is data in the database, which is the whole point:
 * changing a sentence, a deadline or a phone number must never need a code
 * change. The placeholders in the wording are filled from the customer's own
 * record when a notice is raised.
 */
export default function BillingSettings() {
  const [settings, setSettings] = useState([]);
  const [values, setValues] = useState({});
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        fetchData(ApiURLs.BILLING_SETTINGS),
        fetchData(ApiURLs.BILLING_TEMPLATES),
      ]);
      if (s.status === 1) {
        const rows = s.data.result || [];
        setSettings(rows);
        const v = {};
        rows.forEach((r) => { v[r.setting_key] = r.setting_value === null ? "" : r.setting_value; });
        setValues(v);
      }
      if (t.status === 1) {
        const rows = t.data.result || [];
        setTemplates(rows);
        setTemplate(rows.find((x) => x.code === "missed_direct_debit") || rows[0] || null);
      }
    } catch (e) { /* the helper toasts */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await updateData({ settings: values }, ApiURLs.BILLING_SETTINGS);
      if (res.status === 1) { toast.success(res.message); await load(); }
      else toast.error(res.message);
    } catch (e) { /* handled */ }
    setSaving(false);
  };

  const saveTemplate = async () => {
    if (!template) return;
    setSaving(true);
    try {
      const res = await updateData(
        { subject: template.subject, body: template.body, is_active: template.is_active },
        `${ApiURLs.BILLING_TEMPLATES}/${template.code}`
      );
      if (res.status === 1) { toast.success(res.message); await load(); }
      else toast.error(res.message);
    } catch (e) { /* handled */ }
    setSaving(false);
  };

  const runPreview = async () => {
    if (!template) return;
    try {
      const res = await postData({}, `${ApiURLs.BILLING_TEMPLATES}/${template.code}/preview`, false);
      if (res.status === 1) setPreview(res.data);
      else toast.error(res.message);
    } catch (e) { /* handled */ }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>;

  const sendingOn = String(values.billing_notifications_enabled || "0") === "1";
  const blanks = settings
    .filter((s) => ["billing_payment_phone", "billing_dd_form_link", "billing_from_email"].includes(s.setting_key))
    .filter((s) => !String(values[s.setting_key] || "").trim())
    .map((s) => s.label);

  const placeholders = template && template.placeholders ? template.placeholders.split(",") : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, overflow: "auto" }}>
      {blanks.length > 0 && (
        <Alert severity="warning">
          Still to fill in before a notice can go out properly: {blanks.join(", ")}. A blank one leaves a gap in
          the letter where a phone number or a link should be.
        </Alert>
      )}

      <Alert severity={sendingOn ? "success" : "info"}>
        {sendingOn
          ? "Automatic sending is ON. A returned Direct Debit will email the customer."
          : "Automatic sending is OFF. Notices are still generated and stored against the account so you can read them, but nothing is emailed to a customer."}
      </Alert>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Settings</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {settings.map((s) => {
            if (s.kind === "bool") {
              return (
                <Box key={s.setting_key} sx={{ flex: "1 1 320px", minWidth: 300 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={String(values[s.setting_key] || "0") === "1"}
                        onChange={(e) =>
                          setValues({ ...values, [s.setting_key]: e.target.checked ? "1" : "0" })
                        }
                      />
                    }
                    label={s.label}
                  />
                  {s.help && <Typography variant="caption" color="text.secondary" display="block">{s.help}</Typography>}
                </Box>
              );
            }
            return (
              <TextField
                key={s.setting_key}
                label={s.label}
                helperText={s.help || " "}
                size="small"
                multiline={s.kind === "textarea"}
                minRows={s.kind === "textarea" ? 3 : undefined}
                type={s.kind === "number" ? "number" : "text"}
                value={values[s.setting_key] === undefined ? "" : values[s.setting_key]}
                onChange={(e) => setValues({ ...values, [s.setting_key]: e.target.value })}
                sx={{ flex: "1 1 320px", minWidth: 300 }}
              />
            );
          })}
        </Box>
        <Divider sx={{ my: 2 }} />
        <Button variant="contained" onClick={saveSettings} disabled={saving}>Save settings</Button>
      </Paper>

      {template && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Wording: {template.name}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(template.is_active) === 1}
                  onChange={(e) => setTemplate({ ...template, is_active: e.target.checked ? 1 : 0 })}
                />
              }
              label="In use"
            />
          </Box>
          {template.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{template.description}</Typography>
          )}

          <TextField
            label="Subject" fullWidth size="small" sx={{ mb: 2 }}
            value={template.subject}
            onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
          />
          <TextField
            label="Letter" fullWidth multiline minRows={16}
            value={template.body}
            onChange={(e) => setTemplate({ ...template, body: e.target.value })}
            sx={{ "& textarea": { fontFamily: "inherit", fontSize: 14, lineHeight: 1.5 } }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>
            Anything in double braces is replaced with the customer's own details. Click one to copy it.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
            {placeholders.map((p) => (
              <Chip
                key={p} size="small" variant="outlined" label={`{{${p}}}`}
                onClick={() => {
                  navigator.clipboard.writeText(`{{${p}}}`);
                  toast.info(`Copied {{${p}}}`);
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={saveTemplate} disabled={saving}>Save wording</Button>
            <Button variant="outlined" onClick={runPreview}>Preview against a real account</Button>
          </Box>
        </Paper>
      )}

      <Dialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="sm">
        <DialogTitle>{preview && preview.subject}</DialogTitle>
        <DialogContent>
          {preview && preview.unresolved_placeholders && preview.unresolved_placeholders.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nothing filled in these: {preview.unresolved_placeholders.join(" ")}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            {preview && `Against account ${preview.against.account_number || "-"}, invoice ${preview.against.invoice_number || "none"}, to ${preview.to || "no address"}`}
          </Typography>
          <Box sx={{ mt: 1, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}>
            {preview && preview.body}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
